import { isTenantOwnerAdmin } from "utils/isTenantOwnerAdmin"
import React, { useEffect, useState } from "react"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import {
  Card,
  CardBody,
  Col,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import Select from "react-select"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import {
  deleteModifierGroupForAll,
  getAllModifierGroups,
  updateModifierGroupStatus,
} from "store/actions/modifierGroupAction"
import Switch from "components/Shared/Switch"
import { showAlert } from "store/reducers/alertReducer"
import DeleteModal from "components/Common/DeleteModal"
import Paginations from "components/Common/Paginations"
import Filters from "components/Common/Filters"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import placeholderImage from "assets/images/placeholderImage.png"
import DownloadCSV from "components/Shared/DownloadCSV"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import TableShimmer from "components/Shared/TableShimmer"
import { getCountryList } from "store/actions/countriesAction"
import { useTranslation } from "react-i18next"
import ConfirmModal from "components/Common/ConfirmModal"

const ModifierGroups = mode => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Modifiers Groups ")

  const { categoryId, itemId } = useParams()
  const dispatch = useDispatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1

  const navigate = useNavigate()
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [statusPayload, setStatusPayload] = useState(null)

  const {
    loading,
    isLoading,
    isUpdating,
    totalCount,
    itemsPerPage,
    currentPage,
    totalPages,
    modifierGroups,
  } = useSelector(state => state.ModifierGroups)
  const { countryList } = useSelector(state => state.Countries)

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
  })

  const { userRole, accountType } = useSelector(state => state.Login)

  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
    countryCode: [],
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = getSubPermissions(
      userRole,
      4,
      "menus_1",
      accountType
    )
    const isPermissionView = isPermissionEnabled(
      userRole,
      4,
      "menus_1",
      accountType
    )
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      4,
      "menus_2",
      accountType
    )
    const isPermissionEdit = getSubPermissions(
      userRole,
      4,
      "menus_3",
      accountType
    )
    const isPermissionDelete = isPermissionEnabled(
      userRole,
      4,
      "menus_4",
      accountType
    )
    if (
      !isPermissionList?.modifier_access_view?.isEnabled ||
      !isPermissionView
    ) {
      navigate("/dashboard")
    } else {
      getchAllModifierGroups()
    }
    setUsersPermissions({
      list: isPermissionList,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
    })
  }, [userRole])

  useEffect(() => {
    fetchCountryList()
  }, [])

  const getchAllModifierGroups = () => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: status,
      orderBy: sortOrder,
      keyword: searchTerm,
    }
    dispatch(getAllModifierGroups(payload))
    setIsApiCalled(true)
  }

  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Active"), value: 1 },
        { label: t("Deactive"), value: 0 },
      ],
      placeholder: t("All"),
    },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
    {
      name: "countryCode",
      type: "multiSelect",
      options: countryList?.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item.countryCode,
      })),

      placeholder: t("Select Countries"),
    },
  ]

  const fetchCountryList = () => {
    dispatch(getCountryList())
  }

  const openStatusConfirm = (id, status) => {
    setStatusPayload({
      id,
      status: status === 1 ? "0" : "1",
    })
    setSelectedId(id)
    setShowStatusModal(true)
  }

  const handleConfirmStatusUpdate = () => {
    if (!statusPayload) return

    dispatch(updateModifierGroupStatus(statusPayload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .finally(() => {
        setShowStatusModal(false)
        setStatusPayload(null)
      })
  }

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [selectedId, setSelectedId] = useState(null)

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // for delete
    }
    dispatch(deleteModifierGroupForAll(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const getModifierGroupsApiCall = (page = 1) => {
    const { status, sortOrder, countryCode } = selectedFilters
    const updatedCountryCode = countryCode?.map(item => item.value) || []

    const searchParams = new URLSearchParams()
    searchTerm && searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status),
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      countryCode: updatedCountryCode,
    }

    dispatch(getAllModifierGroups(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/menus/modifier-groups?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    getModifierGroupsApiCall(1)
  }

  const onPageChange = page => {
    getModifierGroupsApiCall(page)
  }

  // FILTER
  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     const delayDebounceFn = setTimeout(() => {
  //       handleSearch()
  //     }, 500)

  //     return () => clearTimeout(delayDebounceFn)
  //   }
  // }, [searchTerm])

  // useEffect(() => {
  //   if (isApiCalled) {
  //     handleSearch()
  //   }
  // }, [selectedFilters])

  const handleReset = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2, // 0 deactive, 1 active, 2 for all
      orderBy: 1,
      keyword: "",
    }
    dispatch(getAllModifierGroups(payload))
    setSelectedFilters({
      status: 2,
      sortOrder: 1,
      countryCode: [],
    })
    setSearchTerm("")
    navigate(`/menus/modifier-groups?page=1`)
  }

  const handleExcelDownload = async () => {
    const { status, sortOrder, countryCode } = selectedFilters

    const updatedCountryCode = countryCode?.map(item => item.value) || []

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "MODIFIER-GROUP",
      exportType: "xlsx",
      countryCode: updatedCountryCode,
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Modifier Groups"))
    }
  }

  const exportToCSV = async () => {
    const { status, sortOrder, countryCode } = selectedFilters

    const updatedCountryCode = countryCode?.map(item => item.value) || []

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "MODIFIER-GROUP",
      exportType: "csv",
      countryCode: updatedCountryCode,
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Modifier Groups"))
    }
  }

  return (
    <>
      <div className="page-content modifiers_page">
        <div className="container-fluid">
          <Breadcrumbs />

          <Row>
            <Col xl={12}>
              <Card className="full_height_card">
                <DataExportDropdown
                  cardTitle={t("Modifier Groups")}
                  isAddBtn={!loading && usersPermissions.create}
                  addBtnText={t("Create Modifier Group")}
                  onAddBtnClick={() => navigate("/menus/modifier-groups/add")}
                  isExportData={loading ? false : true}
                  onDownloadCSV={exportToCSV}
                  onDownloadExcel={handleExcelDownload}
                />
                {/* {!loading && ( */}
                <CardBody>
                  <Filters
                    filters={filters}
                    searchTerm={searchTerm}
                    handleSearchChange={handleSearchChange}
                    handleFilterChange={handleFilterChange}
                    handleSearch={handleSearch}
                    onReset={handleReset}
                    selectedFilters={selectedFilters}
                    searchBy={t("Name")}
                    colSize={4}
                  />
                </CardBody>
                {/* )} */}
              </Card>
            </Col>
          </Row>
          <Row className="m-auto">
            {loading ? (
              <Card>
                <TableShimmer row={7} col={3} />
              </Card>
            ) : modifierGroups.length === 0 ? (
              <p className="my-3 text-center">
                {t("No modifiers groups found")}
              </p>
            ) : (
              <Card className="p-0">
                <CardBody className="p-0">
                  <Table className="table mb-0 " hover>
                    <thead className="table-light">
                      <tr>
                        <th className="text-start min_80">
                          <span className="d-inline-block py-2 px-2">
                            {t("Modifier Groups")}
                          </span>
                        </th>
                        <th className="text-start">
                          {(isTenantOwnerAdmin() ||
                            usersPermissions?.edit?.menu_full_access_update
                              ?.isEnabled) && (
                            <span className="d-inline-block py-2">
                              {t("Status")}
                            </span>
                          )}
                        </th>
                        <th className="text-end">
                          <span
                            className="d-inline-block py-2"
                            style={{
                              paddingRight: 20,
                              display: "block",
                            }}
                          >
                            {t("Action")}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {modifierGroups.map(
                        ({ _id, name, nameAr, imageUrl, status, type }) => (
                          <tr key={_id}>
                            <td className="text-start">
                              <div className="left_section ps-2">
                                <Link
                                  to={
                                    type === "sizes"
                                      ? `/menus/modifiers/${_id}/items?modifierType=${type}`
                                      : `/menus/modifiers/${_id}/items`
                                  }
                                >
                                  <div className="d-flex align-items-center">
                                    <img
                                      width={47}
                                      height={44}
                                      src={
                                        imageUrl ? imageUrl : placeholderImage
                                      }
                                      style={{ objectFit: "contain" }}
                                    />
                                    <h6
                                      className="cat_title in_line_one"
                                      style={{ margin: "0 16px" }}
                                    >
                                      {language === "sa" ? nameAr : name}
                                    </h6>
                                  </div>
                                </Link>
                              </div>
                            </td>
                            <td className="text-start">
                              {(isTenantOwnerAdmin() ||
                                usersPermissions?.edit?.menu_full_access_update
                                  ?.isEnabled) && (
                                <div style={{ marginTop: 10 }}>
                                  <Switch
                                    isChecked={status}
                                    disabled={_id === selectedId && isUpdating}
                                    onClickHandler={() =>
                                      openStatusConfirm(_id, status)
                                    }
                                  />
                                </div>
                              )}
                            </td>
                            <td className="text-end">
                              <div
                                className="d-flex justify-content-end align-items-center pe-2"
                                style={{ marginTop: 10 }}
                              >
                                {(isTenantOwnerAdmin() ||
                                  usersPermissions?.edit
                                    ?.menu_full_access_update?.isEnabled) && (
                                  <>
                                    <i
                                      id={`edittooltip_${_id}`}
                                      onClick={() => {
                                        navigate(
                                          `/menus/modifier-groups/edit?id=${_id}`
                                        )
                                      }}
                                      className="mdi mdi-pencil font-size-18 text-success mx-2 cursor-pointer"
                                    ></i>
                                    <UncontrolledTooltip
                                      placement="top"
                                      target={`edittooltip_${_id}`}
                                    >
                                      {t("Edit")}
                                    </UncontrolledTooltip>
                                  </>
                                )}
                                {usersPermissions.list && (
                                  <>
                                    <i
                                      id={`viewtooltip_${_id}`}
                                      onClick={() => {
                                        navigate(
                                          `/menus/modifier-group-details?id=${_id}`
                                        )
                                      }}
                                      className="mdi mdi-eye font-size-18 text-black mx-2 cursor-pointer"
                                    ></i>
                                    <UncontrolledTooltip
                                      placement="top"
                                      target={`viewtooltip_${_id}`}
                                    >
                                      {t("View")}
                                    </UncontrolledTooltip>
                                  </>
                                )}
                                {usersPermissions.delete && (
                                  <>
                                    <i
                                      id={`deletetooltip_${_id}`}
                                      onClick={e => {
                                        setItemIdToDelete(_id)
                                        setShowDeleteModal(true)
                                      }}
                                      className="bx bxs-trash font-size-18 mx-2 cursor-pointer"
                                      style={{ color: "#FD5F62" }}
                                    ></i>
                                    <UncontrolledTooltip
                                      placement="top"
                                      target={`deletetooltip_${_id}`}
                                    >
                                      {t("Delete")}
                                    </UncontrolledTooltip>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            )}
            {/* {modifierGroups.map(item => (
              <Col xl={2} sm={6} key={item._id}>
                <Card className="full_height_card">
                  <CardBody>
                    
                    <Link to={`/menus/modifiers/${item._id}/items`}>
                      <img
                        src={item.imageUrl ? item.imageUrl : placeholderImage}
                        className="w-100 modifier_img item_img"
                        height={112}
                      />
                    </Link>
                    <div className="inner d-flex align-items-center justify-content-between mt-3">
                      <h5 className="title in_2_line fs-6">{item.name}</h5>
                      <div className="d-flex">
                        {usersPermissions.edit && (
                          <Switch
                            isChecked={item.status}
                            disabled={item._id === selectedId && isUpdating}
                            onClickHandler={() =>
                              handleStatusClick(item._id, item.status)
                            }
                          />
                        )}
                        <UncontrolledDropdown>
                          <DropdownToggle tag="a" className="card-drop">
                            <i className="mdi mdi-dots-vertical font-size-18"></i>
                          </DropdownToggle>
                          <DropdownMenu className="dropdown-menu-end">
                            {usersPermissions.edit && (
                              <DropdownItem
                                onClick={() => {
                                  navigate(
                                    `/menus/modifier-groups/edit?id=${item._id}`
                                  )
                                }}
                              >
                                <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                                Edit
                              </DropdownItem>
                            )}
                            {usersPermissions.list && (
                              <DropdownItem
                                onClick={() => {
                                  navigate(
                                    `/menus/modifier-group-details?id=${item._id}`
                                  )
                                }}
                              >
                                <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
                                View
                              </DropdownItem>
                            )}
                            {usersPermissions.delete && (
                              <DropdownItem
                                onClick={e => {
                                  setItemIdToDelete(item._id)
                                  setShowDeleteModal(true)
                                }}
                              >
                                <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                                Delete
                              </DropdownItem>
                            )}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))} */}
          </Row>
          {!loading && (
            <Card>
              <CardBody>
                <Paginations
                  data={modifierGroups}
                  itemsPerPage={itemsPerPage}
                  onPageChange={onPageChange}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalCount={totalCount}
                />
              </CardBody>
            </Card>
          )}
        </div>
      </div>
      <DeleteModal
        show={showDeleteModal}
        onDeleteClick={handleDelete}
        isLoading={isLoading}
        onCloseClick={() => {
          setItemIdToDelete(null)
          setShowDeleteModal(false)
        }}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Modifier Group"),
        })}
      />
      <ConfirmModal
        show={showStatusModal}
        onDeleteClick={handleConfirmStatusUpdate}
        isLoading={isUpdating}
        onCloseClick={() => {
          setShowStatusModal(false)
          setStatusPayload(null)
        }}
        buttonText={t("Yes")}
        bodyTitle={t("Are you sure you want to update the status?")}
      />
    </>
  )
}

export default ModifierGroups
